import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Tenant {
  id: string; name: string; slug: string;
  bgRemovalMode: 'IMGLY' | 'REMOVE_BG';
  maxCars: number;
  hasRemoveBgKey: boolean;
}

interface ShotSlot {
  key: string; label: string; instructions: string;
  guide: string; order: number; enabled: boolean;
}

export function SettingsPage() {
  const user = useAuth((s) => s.user)!;
  const canEdit = user.isSuperAdmin || user.role === 'TENANT_ADMIN' || user.role === 'MANAGER';

  return (
    <div className="container py-8 space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Dealership-wide preferences.</p>
      </header>

      <Tabs defaultValue="tenant" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tenant">Dealership</TabsTrigger>
          <TabsTrigger value="package">Photo package</TabsTrigger>
        </TabsList>
        <TabsContent value="tenant"><TenantSettings canEdit={canEdit} /></TabsContent>
        <TabsContent value="package"><PhotoPackageEditor canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}

function TenantSettings({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const t = useQuery({ queryKey: ['tenant-self'], queryFn: () => api.get<Tenant>('/tenant') });

  const [name, setName] = useState('');
  const [maxCars, setMax] = useState(100);
  const [bgMode, setBgMode] = useState<'IMGLY' | 'REMOVE_BG'>('IMGLY');
  const [removeBgApiKey, setKey] = useState('');
  const [clearKey, setClear] = useState(false);

  useEffect(() => {
    if (!t.data) return;
    setName(t.data.name);
    setMax(t.data.maxCars);
    setBgMode(t.data.bgRemovalMode);
  }, [t.data]);

  const saveMut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { name, maxCars, bgRemovalMode: bgMode };
      if (clearKey) body.removeBgApiKey = '';
      else if (removeBgApiKey.trim()) body.removeBgApiKey = removeBgApiKey.trim();
      return api.patch('/tenant', body);
    },
    onSuccess: () => {
      toast.success('Saved');
      setKey(''); setClear(false);
      qc.invalidateQueries({ queryKey: ['tenant-self'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Save failed'),
  });

  if (!t.data) return <Card><CardContent className="py-8 text-muted-foreground">Loading…</CardContent></Card>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dealership</CardTitle>
        <CardDescription>{t.data.slug}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} />
          </div>
          <div className="space-y-2">
            <Label>Max active cars</Label>
            <Input type="number" value={maxCars} onChange={(e) => setMax(Number(e.target.value))} disabled={!canEdit} min={10} max={10000} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Background removal</Label>
          <Select value={bgMode} onValueChange={(v) => setBgMode(v as 'IMGLY' | 'REMOVE_BG')} disabled={!canEdit}>
            <SelectTrigger className="sm:w-80"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="IMGLY">Browser-side (free, runs on device)</SelectItem>
              <SelectItem value="REMOVE_BG">remove.bg API (paid, requires key)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {bgMode === 'REMOVE_BG' && (
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <Label>remove.bg API key {t.data.hasRemoveBgKey && <span className="text-xs text-emerald-400">· configured</span>}</Label>
            <Input type="password" placeholder={t.data.hasRemoveBgKey ? '••••••••' : 'Paste your API key'} value={removeBgApiKey} onChange={(e) => setKey(e.target.value)} disabled={!canEdit} />
            {t.data.hasRemoveBgKey && canEdit && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={clearKey} onChange={(e) => setClear(e.target.checked)} />
                Clear the saved key
              </label>
            )}
          </div>
        )}
        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PhotoPackageEditor({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const pkg = useQuery({ queryKey: ['photo-package'], queryFn: () => api.get<ShotSlot[]>('/tenant/photo-package') });

  const [shots, setShots] = useState<ShotSlot[] | null>(null);
  useEffect(() => {
    if (pkg.data) setShots(pkg.data.map((s) => ({ ...s })));
  }, [pkg.data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const saveMut = useMutation({
    mutationFn: () => {
      if (!shots) return Promise.resolve();
      const reorderedWithOrder = shots.map((s, i) => ({ ...s, order: i + 1 }));
      return api.put('/tenant/photo-package', { shots: reorderedWithOrder });
    },
    onSuccess: () => {
      toast.success('Photo package saved');
      qc.invalidateQueries({ queryKey: ['photo-package'] });
    },
  });

  if (!shots) return <Card><CardContent className="py-8 text-muted-foreground">Loading…</CardContent></Card>;

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id || !shots) return;
    const from = shots.findIndex((s) => s.key === active.id);
    const to = shots.findIndex((s) => s.key === over.id);
    setShots(arrayMove(shots, from, to));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Photo package</CardTitle>
        <CardDescription>Drag to reorder. Toggle to enable/disable individual shots.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={shots.map((s) => s.key)} strategy={verticalListSortingStrategy}>
            <ol className="space-y-1">
              {shots.map((s, idx) => (
                <SortableShot
                  key={s.key}
                  shot={s}
                  index={idx}
                  canEdit={canEdit}
                  onToggle={(v) => setShots(shots.map((x) => (x.key === s.key ? { ...x, enabled: v } : x)))}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
        {canEdit && (
          <div className="flex justify-end pt-2">
            <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? 'Saving…' : 'Save order'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SortableShot({ shot, index, canEdit, onToggle }: {
  shot: ShotSlot; index: number; canEdit: boolean; onToggle: (v: boolean) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: shot.key });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 ${isDragging ? 'opacity-60 ring-2 ring-primary' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        disabled={!canEdit}
        className="cursor-grab text-muted-foreground hover:text-foreground disabled:cursor-not-allowed"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="w-6 text-center text-xs text-muted-foreground">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm font-medium">{shot.label}</div>
        <div className="truncate text-xs text-muted-foreground">{shot.instructions}</div>
      </div>
      <Switch checked={shot.enabled} onCheckedChange={onToggle} disabled={!canEdit} />
    </li>
  );
}
